import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import formatDate from "../../util/dateFormat";
import { formatDate1, getEncodedValue } from "../forms/utility";
import { APIConstants } from "../../constants/ApiConstants";
import { useAppSelector } from "../../redux/hooks";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import theme from "../../theme";
import useButtonStyles from "../../styles/useButtonStyles";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { debounce } from "../../util/debounce";
import InfiniteTable from "../Table/InfiniteScroll/InfiniteTable";
import useGetTotalRecords from "../../Hooks/CustomHooks/UseGetTotalRecords";
import {
  SearchToolTip,
  filterConstants,
  DateConstants,
  permissionsConstants,
} from "../../constants/AppConstants";
import SearchInput from "../../Shared/Components/SearchInput/SearchInput";
import { usePermissionGetter } from "../../Hooks/Permissions/userPermissionGetter";
interface TableColumn {
  key: string;

  header: string;

  searchable?: boolean;

  filterable?: boolean;
}
const columns: TableColumn[] = [
  { key: "itemImage", header: "Image" },
  { key: "itemName", header: "Item" },
  { key: "itemPrice", header: "Selling Price" },
  { key: "totalQuantity", header: "Total Quantity" },
  { key: "availableQuantity", header: "Available Quantity" },
  { key: "fromDate", header: "Sales Start Date" },
  { key: "toDate", header: "Sales Expiry Date " },
  { key: "activationDate", header: "Activation Date" },
];
const metaData = {
  itemImage: {
    isImage: true,
    linkTo: "",
  },
  itemName: {
    isImage: false,
    linkTo: "",
  },
  itemPrice: {
    isImage: false,
    linkTo: "",
  },
  totalQuantity: {
    isImage: false,
    linkTo: "",
  },
  soldQuantity: {
    isImage: false,
    linkTo: "",
  },
  fromDate: {
    isImage: false,
    linkTo: "",
  },
  toDate: {
    isImage: false,
    linkTo: "",
  },
  activationDate: {
    isImage: false,
    linkTo: "",
  },
  flashStatus: {
    isImage: false,
    linkTo: "",
  },
};

const FlashSalesActive = () => {
  const { getUserPermission: activeFlashSalesPermissions } =
    usePermissionGetter(
      permissionsConstants.flash_sale,
      permissionsConstants.active_flash_sales
    );

  const buttonStyles = useButtonStyles();
  const [activeFlashSale, setActiveFlashSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [salesStartDate, setSalesStartDate] = useState("");
  const [salesExpiryDate, setSalesExpiryDate] = useState("");
  const [activiationDate, setActivationDate] = useState("");
  const [filterActive, setFilterActive] = useState(false);
  const [isResetApiCall, setIsResetApiCall] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>();
  const { selectedMerchatDetails } = useAppSelector(
    (state: any) => state.persistReducers.getMerchantList
  );
  const { merchantId } = selectedMerchatDetails;
  const { totalCount, getTotalCounts } = useGetTotalRecords();

  const handleClose = () => {
    setOpenFilter(false);
  };

  const resetFields = () => {
    setSalesStartDate("");
    setSalesExpiryDate("");
    setActivationDate("");
    setFilterActive(false);
    setIsResetApiCall(true);
  };

  useEffect(() => {
    if (isResetApiCall) getFlashSales();
  }, [isResetApiCall]);

  const getFlashSales = async (searchValue?: string) => {
    let newSearchValue = encodeURIComponent(searchValue ? searchValue : "");
    try {
      setLoading(true);
      let url =
        process.env.REACT_APP_API_URL +
        APIConstants.getMerchantDeals +
        `?filter=flashsales&MerchantID=${merchantId}${
          newSearchValue ? `&QuerySearch=${newSearchValue}` : ""
        }${salesStartDate ? `&ValidityStartDate=${salesStartDate}` : ""}${
          salesExpiryDate ? `&ValidityEndDate=${salesExpiryDate}` : ""
        }${activiationDate ? `&ActivationDate=${activiationDate}` : ""}`;
      // if (activeFlashSale.length === 0)
      //   getTotalCounts(url, "data.Result.data.flashsales");
      const deals = await axios.get(url);
      const data = deals.data.Result.data.flashsales;
      const mappedData = data
        .filter((sales: any) => sales.ActivatedOrNot === filterConstants.Active)
        .map((sales: any) => ({
          itemImage: sales.ProductImage ? sales.ProductImage : "-",
          itemName: sales.ProductName ? sales.ProductName : "-",
          itemPrice: sales.Price ? sales.Price : "-",
          totalQuantity: sales.TotalQTY ? sales.TotalQTY : "-",
          availableQuantity: sales.AvailableQty ? sales.AvailableQty : "-",
          soldQuantity: sales.SoldQty ? sales.SoldQty : "-",
          fromDate: sales.SalesStartDate
            ? formatDate(sales.SalesStartDate)
            : "-",
          toDate: sales.SalesExpiryDate
            ? formatDate(sales.SalesExpiryDate)
            : "-",
          activationDate: sales.ActivationDateTime
            ? formatDate1(sales.ActivationDateTime)
            : "-",
        }));
      setActiveFlashSales(mappedData);
    } catch (error) {
      console.error(error);
      setLoading(false);
    } finally {
      setLoading(false);
      setIsResetApiCall(false);
    }
    if (activiationDate || salesExpiryDate || salesStartDate) {
      setFilterActive(true);
    }
  };

  const getSearchFlashSaleDebounce = useCallback(
    debounce(getFlashSales, 1000),
    []
  );

  const handleSearch = () => {
    const searchValue = searchInputRef.current
      ? getEncodedValue(searchInputRef.current.value)
      : "";
    setActiveFlashSales([]);
    getFlashSales(searchValue);
  };

  useEffect(() => {
    getFlashSales();
  }, []);

  return (
    <Grid>
      <Grid display={"flex"} justifyContent={"end"} px={4} gap={2} py={4}>
        <SearchInput
          HelpTip={SearchToolTip.Sales}
          onCallBack={handleSearch}
          ref={searchInputRef}
        />
        {activeFlashSalesPermissions("read") && (
          <Button
            variant="text"
            startIcon={
              filterActive ? (
                <FilterAltIcon sx={{ color: theme.color?.secondary }} />
              ) : (
                <FilterAltOutlinedIcon />
              )
            }
            sx={{
              color: theme.color?.newFilterGrey,
              textTransform: "capitalize",
            }}
            onClick={() => {
              setOpenFilter(true);
            }}
          >
            Filter
          </Button>
        )}

        <Dialog onClose={handleClose} open={openFilter} sx={{ ml: "60vw" }}>
          <Grid sx={{ width: "483px", py: 4, px: 2 }}>
            <DialogTitle
              display={"flex"}
              justifyContent={"space-between"}
              borderBottom={theme.border?.smallGrey}
            >
              <Typography
                sx={{
                  fontFamily: theme.font?.fontFamily.primary,
                  color: theme.color?.primary,
                  fontSize: theme.font?.fontSize.large,
                  fontWeight: theme.font?.fontWeight.thicker,
                }}
              >
                Filter
              </Typography>
              <IconButton onClick={() => setOpenFilter(false)}>
                <CloseOutlinedIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ borderBottom: theme.border?.smallGrey }}>
              <Grid borderBottom={theme.border?.newGreyBorder} py={4}>
                <Typography
                  sx={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontSize: theme.font?.fontSize.medium,
                    fontWeight: theme.font?.fontWeight.thick,
                  }}
                >
                  Sales Start Date
                </Typography>
                <Grid display={"flex"} mt={2} gap={2} width={"70%"}>
                  <TextField
                    value={salesStartDate}
                    onChange={(e) => setSalesStartDate(e.target.value)}
                    type="date"
                    size="small"
                    fullWidth
                    InputProps={{
                      inputProps: {
                        max: DateConstants.maxDate,
                      },
                    }}
                  ></TextField>
                </Grid>
              </Grid>
              <Grid borderBottom={theme.border?.newGreyBorder} py={4}>
                <Typography
                  sx={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontSize: theme.font?.fontSize.medium,
                    fontWeight: theme.font?.fontWeight.thick,
                  }}
                >
                  Sales Expiry Date
                </Typography>
                <Grid display={"flex"} mt={2} gap={2} width={"70%"}>
                  <TextField
                    value={salesExpiryDate}
                    onChange={(e) => setSalesExpiryDate(e.target.value)}
                    type="date"
                    size="small"
                    fullWidth
                    InputProps={{
                      inputProps: {
                        max: DateConstants.maxDate,
                      },
                    }}
                  ></TextField>
                </Grid>
              </Grid>
              <Grid py={4} pb={0}>
                <Typography
                  sx={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontSize: theme.font?.fontSize.medium,
                    fontWeight: theme.font?.fontWeight.thick,
                  }}
                >
                  Activation Date
                </Typography>
                <Grid display={"flex"} mt={2} gap={2} width={"70%"}>
                  <TextField
                    value={activiationDate}
                    onChange={(e) => setActivationDate(e.target.value)}
                    type="date"
                    size="small"
                    fullWidth
                    InputProps={{
                      inputProps: {
                        max: DateConstants.maxDate,
                      },
                    }}
                  ></TextField>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ pr: 4, gap: 1, pt: 3 }}>
              <Button
                sx={{ ...buttonStyles.outlinedSecondary }}
                onClick={() => resetFields()}
              >
                Reset
              </Button>
              <Button
                sx={{ ...buttonStyles.containedSecondary }}
                onClick={() => {
                  setActiveFlashSales([]);
                  getFlashSales();
                  setOpenFilter(false);
                }}
              >
                Apply
              </Button>
            </DialogActions>
          </Grid>
        </Dialog>
      </Grid>
      {activeFlashSalesPermissions("read") && (
        <InfiniteTable
          header={columns}
          contentItems={activeFlashSale}
          metaData={metaData}
          hasMoreRecords={false}
          isInitialLoading={activeFlashSale.length === 0 && loading}
          totalRecords={activeFlashSale?.length}
        />
      )}
    </Grid>
  );
};

export default FlashSalesActive;
